export const SKU_TYPES = ['incremental', 'random']
export const SKU_FORMATS = ['numeric', 'alpha-numeric']

// Bounds for the "Number of Digits" stepper in SKU Generator settings.
// Lower bound is 5 (CDP-2347) — legacy customers run 5-digit SKU workflows;
// the actual per-row minimum auto-bumps once the next SKU number outgrows 5
// digits (logic lives in Sku.vue).
export const SKU_MIN_DIGITS = 5
export const SKU_MAX_DIGITS = 12

// SKU may contain ONLY A–Z, a–z, 0–9, hyphen, underscore and period — the set
// eBay carries safely. Everything else (spaces, `#`, `/`, `%`, `&`,
// `?`, `+`, `@`, quotes, other punctuation, emoji, accented/non-ASCII letters)
// is invalid.
//
// The line is drawn at URL safety, not at eBay's error text. eBay publishes no
// whitelist and its API can answer "only alphanumeric characters can be used",
// but `-`, `_` and `.` pass in practice: Inventory API resources are addressed
// by SKU in the PATH (`GET /inventory_item/{sku}`), and those three are
// unreserved characters in RFC 3986, so they survive every hop unencoded. The
// characters that genuinely break it are the ones needing percent-encoding
// there — an encoded `/` is rejected at the web-server layer before the API
// sees it, which is the silent-sync-failure class legacy `#` SKUs hit.
//
// Two patterns on purpose: a `/g` regex is stateful under `.test()` (its
// lastIndex advances between calls), so validation and stripping use separate
// instances. Consumed by the helpers in `@/helpers/sku`.
export const SKU_INVALID_CHAR_PATTERN = /[^A-Za-z0-9._-]/
export const SKU_INVALID_CHAR_PATTERN_GLOBAL = /[^A-Za-z0-9._-]/g

// Shape of a SKU the API has already suffixed with a base-36 card id
// (`47690-13oej6`). Matched by SHAPE, not against the card's own id, because
// the API has emitted SKUs carrying a neighbouring card's id.
//
// A seller can type hyphens now too, so a hand-made SKU ending in
// `-<letters/digits>` (`2024-TOPPS`) now matches this as well. The cost is
// display-only and one-directional: `skuIdSuffix` returns '' and the id simply
// isn't appended on screen. Nothing is written, and the alternative — trusting
// the card's own id — is the case this pattern replaced.
export const SUFFIXED_SKU_PATTERN = /-[0-9a-z]+$/i

// Longest SKU a user can enter, enforced on every SKU input (`maxlength`) and
// on the AG Grid clipboard path, which has no input element to enforce it.
// eBay documents 50 as its own limit for SKU / Custom Label.
export const SKU_MAX_LENGTH = 50

// The allowed set, spelled out for humans. One sentence, reused by every SKU
// error so the rule always reads the same way.
const SKU_ALLOWED_CHARS_SENTENCE =
  'SKUs can only contain letters, numbers, hyphens, underscores, and periods.'

// Inline field error shown while typing/pasting an invalid character. Names the
// offending character: "remove the bad character" is only actionable if the
// reader can tell WHICH one it was, and a pasted string can hide it in the
// middle. Falls back to the plain rule when there is no single character to
// point at.
export const skuInvalidCharError = (character) =>
  character
    ? `${SKU_ALLOWED_CHARS_SENTENCE} Remove the "${character}".`
    : SKU_ALLOWED_CHARS_SENTENCE

// Inline field error for a SKU that is too long. Separate from the character
// error because the fix is different — shorten it, not clean it.
export const SKU_TOO_LONG_ERROR = `SKUs can't be longer than ${SKU_MAX_LENGTH} characters.`

// Per-item error shown when an item with an invalid SKU can't be listed.
export const SKU_LISTING_BLOCKED_ERROR =
  `This item can't be listed — its SKU contains invalid characters. ${SKU_ALLOWED_CHARS_SENTENCE}`

// How long (ms) the inline "invalid character" hint stays visible after a
// rejected keystroke/paste before auto-hiding, across every SKU input.
export const SKU_ERROR_VISIBLE_MS = 3000

export const DATA_FIELDS_TITLE = [
  'category', 'type', 'brand', 'set', 'card', 'name', 'subset', 'variation',
  'attribute', 'team', 'grade', 'grader', 'numerical', 'condition', 'sku', 'cert', 'sn', 'note', 'rookie', 'auto',
  'cabinet', 'shelf', 'box', 'row', 'section'
]

export const DATA_FIELDS_DESCRIPTION = [
  'category', 'type', 'year', 'brand', 'set', 'card', 'name', 'subset', 'variation',
  'attribute', 'team', 'grade', 'grader', 'numerical', 'condition', 'sku', 'cert', 'sn', 'note', 'rookie', 'auto',
  'cabinet', 'shelf', 'box', 'row', 'section'
]

export const PLACEMENTS = ['top', 'bottom']

export const BUY_ADDONS_STEPS = ['Select Amount', 'Choose payment method']
export const CHANGE_PLAN_STEPS = ['Select Plan', 'Choose payment method']

export const NOTIFICATIONS = [
  {
    label: 'Privacy and legal updates',
    value: 'legal',
    description: 'Receive emails when we have important updates to share regarding our privacy policy or terms of service',
  },
  {
    label: 'CDP Changelog',
    value: 'changelog',
    description: 'CDP is constantly improving. Subscribe to the changelog to learn about new features and improvements.',
  },
  {
    label: 'CDP Newsletter',
    value: 'newsletter',
    description: 'CDP is constantly improving. Subscribe to the newsletter to learn about new features and improvements.',
  },
  {
    label: 'Daily Sale Recap',
    value: 'export',
    description: 'Receive a summary of the previous 24h sales activity, including items sold and inventory location.',
  },
  {
    label: 'Import Completion',
    value: 'import',
    description: 'Get an email when your inventory import has finished processing.',
  },
  {
    label: 'Publishing Completion',
    value: 'sales',
    description: 'Get notified when your selected listings have finished publishing to connected marketplaces.',
  },
]